import { RequestStatus } from '@common/enums/requestStatus';
import type { ICustomControlProcedureDiagnosticReportReadProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { SomnologyOrganizationType } from '../../../../../../common/enums/somnologyOrganizationType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { watchpatSleepRecorderTypes } from '../../../../../../common/validation/customControls/somnology';
import { translateKey } from '../../../../../translate';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { ProcedureDiagnosticReportReadButtons } from '../../../../elements/carePathButtons/ProcedureDiagnosticReportReadButtons';
import { ButtonContainer } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { ProcedureAssetThumbnails, ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { ReloadEpisode } from '../../../episodes/reloadEpisode';
import { OsasoCreateRequestOsasc2Button } from './OsasoCreateOsasc2Button';
import { getKsyosMedicalCenterAssets } from './OsasoV1ProcedureRead';
import { OsasoV1QuestionnairesRead } from './OsasoV1QuestionnairesRead';

export function OsasoV1ProcedureDiagnosticReportRead(props: ICustomControlProcedureDiagnosticReportReadProps) {
    const { patient } = useEpisodeContext();
    const assets =
        props.request.customControlData?.somnologyOrganizationType === SomnologyOrganizationType.ksyosMedicalCenter &&
        !watchpatSleepRecorderTypes.includes(props.request.customControlData.typeSlaaprecorder)
            ? getKsyosMedicalCenterAssets(props.procedure.customControlData)
            : props.procedure.assets;

    const showDiagnosticReportReadButtons =
        !props.request.customControlData ||
        props.request.customControlData.somnologyOrganizationType !== SomnologyOrganizationType.ksyosMedicalCenter ||
        watchpatSleepRecorderTypes.includes(props.request.customControlData.typeSlaaprecorder);

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.process.procedure && props.process.procedure.dateSent}
                isFirstItem
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                organizationName={
                    patient.authorization?.canSeePatientDetails && props.process.procedure?.performerOrganization?.name
                        ? props.process.procedure.performerOrganization.name
                        : undefined
                }
                practitionerName={
                    patient.authorization?.canSeePatientDetails && props.process.procedure?.performerPractitioner
                        ? getDisplayName(props.process.procedure.performerPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                title={getServiceLabel(props.process.code, 'procedure', props.process.name)}
            />

            <HorizontalFormGroup>
                <FormLabel>{translateKey('request.attachment')}</FormLabel>
                <ProcedureAssetThumbnails
                    assets={assets}
                    procedureId={props.procedure.id}
                />
            </HorizontalFormGroup>

            <OsasoV1QuestionnairesRead {...props} />

            {showDiagnosticReportReadButtons && <ProcedureDiagnosticReportReadButtons {...props} />}

            {props.request.status === RequestStatus.completed && (
                <ButtonContainer
                    alignRight
                    buttons={[
                        <OsasoCreateRequestOsasc2Button
                            reloadProcess={async () => await props.reloadProcess(ReloadEpisode.reloadAll)}
                            request={props.request}
                        />,
                    ]}
                />
            )}
        </ProcessContainer>
    );
}
